import { AuthService } from './../services/auth.service';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    private authService = inject(AuthService);
    private router = inject(Router);


    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        // requisições do SharedService (visual web) já trazem o header próprio
        // e têm o refresh delas — aqui só cuidamos do que sai sem Authorization.
        const managed = !request.headers.has('Authorization');
        if (!managed) {
            return next.handle(request);
        }

        // O token só vai para a nossa API. Registrado globalmente, este
        // interceptor mandava o Bearer também para viaCEP, BrasilAPI e IBGE.
        if (!this.isOurApi(request.url)) {
            return next.handle(request);
        }

        // add authorization header with jwt token if available
        const sent_token = this.authService.getTokenData()?.access_token;
        const authorized = this.withToken(request, sent_token);

        return next.handle(authorized).pipe(
            catchError((error: HttpErrorResponse) => {
                // O 401 da própria troca de token não pode disparar outra troca.
                if (error.status !== 401 || this.isTokenRequest(request)) {
                    return throwError(() => error);
                }

                // O backend mata o access anterior assim que o refresh roda,
                // entao requisicoes que ja estavam no ar voltam 401 com token
                // velho. Se o guardado ja e outro, alguem renovou: basta
                // repetir. Renovar de novo aqui rotacionaria o refresh a toa e
                // mataria o access que acabou de nascer, em cascata.
                const current = this.authService.getTokenData()?.access_token;
                if (current && current !== sent_token) {
                    return next.handle(this.withToken(request, current));
                }

                return this.authService.refreshToken().pipe(
                    switchMap((renewed) => {
                        if (!renewed) {
                            this.authService.logout();
                            this.router.navigate(['/login']);
                            return throwError(() => error);
                        }

                        const access_token = this.authService.getTokenData()?.access_token;
                        return next.handle(this.withToken(request, access_token));
                    }),
                );
            }),
        );
    }

    private withToken(request: HttpRequest<unknown>, access_token?: string): HttpRequest<unknown> {
        if (!access_token) {
            return request;
        }
        return request.clone({
            setHeaders: {
                'Authorization': `Bearer ${access_token}`
            }
        });
    }

    private isOurApi(url: string): boolean {
        const base = environment.api.baseUrl;
        return !!base && url.startsWith(base);
    }

    private isTokenRequest(request: HttpRequest<unknown>): boolean {
        return request.url.includes(environment.api.endpoints.login);
    }
}
